/**
 * 보안 접속 토글 — **`wl.viewster.io` 를 백오피스가 이어받은 자리**
 * (2026-09-16 사용자 지정). 로컬에서 Viewster staging·admin에 접속하는 도구이며
 * 백오피스의 로그인·API 이용과는 독립이다(2026-10-03 사용자 확정).
 *
 * 켜면 **지금 이 브라우저가 나가는 주소**가 정해진 시간(서버가 `hours` 로
 * 알려 준다) 동안 열리고, 끄거나 시간이 지나면 닫힌다.
 *
 * ## 화면이 반드시 말해야 하는 것
 *
 * | 무엇 | 왜 |
 * |:--|:--|
 * | 여는 것은 **주소**이지 계정이 아니다 | 같은 회선을 쓰는 사람도 함께 열린다 |
 * | 언제 꺼지나 | 「켜 뒀는데 왜 안 되지」를 없앤다 |
 * | **아직 실제로는 안 열린다**(`enforced` 거짓) | 서버 스위치가 꺼진 동안 켜진 것처럼 보이면 안 된다 |
 *
 * ⚠ **켜고 끈 것이 WAF 에 닿기까지 10초 남짓 든다**(2026-09-17 실측). `title` 이
 * 그 숫자를 말한다 — 안 말하면 「껐는데 열린다」가 버그 신고로 온다.
 * (2026-09-17 지시로 **성공 토스트를 걷었고**, 그 문장을 `title` 로 옮겼다.)
 *
 * ⚠ **「껐다」를 「아무도 못 들어온다」로 적지 마라.** 지금 구조에서 실제로
 * 끊기는 것은 IP 이고, 옛 포털이 심은 공유 쿠키는 그 비밀을 회전하기 전까지
 * 살아 있다. 그 한계를 `title` 에 적어 둔다.
 */

import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/** 서버 `access_gate.MAX_SLOTS_PER_SESSION` 과 같다 — 안내 문구에만 쓴다. */
const MAX_SLOTS_PER_DEVICE = 3;

import {
  closeAccess,
  errorMessage,
  getAccess,
  NotLoggedInError,
  openAccess,
  type AccessState,
} from './api';

/** 남은 시간을 사람 말로. **분 아래는 안 쓴다** — 초가 흐르면 눈이 그리 간다. */
export function remaining(expiresAt: string | null, now: number = Date.now()): string {
  if (!expiresAt) return '';
  const left = new Date(expiresAt).getTime() - now;
  if (left <= 0) return '곧 꺼짐';
  const minutes = Math.floor(left / 60000);
  if (minutes < 60) return `${minutes}분 남음`;
  return `${Math.floor(minutes / 60)}시간 ${minutes % 60}분 남음`;
}

export function AccessToggle({
  onUnauthorized,
}: {
  onUnauthorized: () => void;
}) {
  const [state, setState] = useState<AccessState | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * 남은 시간을 재는 기준 시각.
   *
   * ⚠ **렌더에서 `Date.now()` 를 부르지 않는다.** 그러면 같은 상태인데
   * 그릴 때마다 값이 달라져 React 가 결과를 못 믿는다(eslint 가 막는다).
   */
  const [now, setNow] = useState(() => Date.now());

  /* 상태를 읽고 1분마다 다시 읽는다 — 알림 종과 같은 주기다.
     **실패는 조용히 넘긴다**: 1분마다 도는 요청이라 토스트를 띄우면 서버가
     잠깐 흔들릴 때 화면이 오류로 덮인다. 로그인 만료만 알린다.
     다시 읽는 것이 곧 **만료 감시**다 — 화면만 「곧 꺼짐」으로 두면 실제로
     꺼졌는지 알 길이 없다. */
  useEffect(() => {
    let cancelled = false;
    async function read() {
      try {
        const next = await getAccess();
        if (cancelled) return;
        setState(next);
        setNow(Date.now());
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) onUnauthorized();
      }
    }
    void read();
    const timer = window.setInterval(() => void read(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [onUnauthorized]);

  async function toggle(allDevices = false) {
    if (busy || !state) return;
    setBusy(true);
    try {
      const next = allDevices ? await closeAccess(true) : state.on ? await closeAccess() : await openAccess();
      setState(next);
      setNow(Date.now());
      // **성공은 알리지 않는다**(2026-09-17 지시). 단추 글자가 곧 상태라
      // (「보안 접속」 ↔ 「보안 접속 켜짐 · N분 남음」) 토스트는 같은 말을
      // 한 번 더 하는 자리였다.
      //
      // ⚠ 그래서 **반영 지연(WAF 에 닿기까지 켜기 +10초 · 끄기 +6초, 2026-09-17
      // 실측)을 `title` 로 옮겼다.** 그 숫자를 아무 데도 안 적으면 「껐는데
      // 아직 열린다」가 버그 신고로 온다 — 토스트만 지우지 마라.
      //
      // **실패는 그대로 알린다.** 단추가 원래 자리로 돌아갈 뿐이라, 말하지
      // 않으면 눌렀는데 아무 일도 안 난 것으로 보인다.
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      toast.error(`보안 접속을 바꾸지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  // 아직 못 읽었으면 자리를 안 만든다 — 빈 단추가 깜빡이는 것보다 낫다.
  if (!state) return null;

  const left = remaining(state.expires_at, now);
  const label = state.on ? `보안 접속 켜짐${left ? ` · ${left}` : ''}` : '보안 접속';
  const title = state.on
    ? `${state.ips.join(' · ')}의 스테이징·어드민 접속이 열려 있다. 끄면 이 기기가 연 주소를 모두 닫는다. 다른 기기의 승인은 유지된다.` +
      ' 실제로 막히기까지 10초쯤 걸린다.' +
      (state.enforced ? '' : ' ⚠ 서버 스위치가 꺼져 있어 아직 실제로는 안 열린다.')
    : `누르면 ${state.client_ip ?? '이 주소'}의 스테이징·어드민 접속을 ${state.hours}시간 동안 연다.` +
      ' 여는 것은 주소라 같은 회선을 쓰는 사람도 함께 열린다.' +
      ` 이 기기가 다른 망에서 켜 둔 주소는 그대로 둔다(기기당 ${MAX_SLOTS_PER_DEVICE}곳까지).` +
      ' 실제로 열리기까지 10초쯤 걸린다.';

  return (
    <div className="flex items-center gap-s2">
    <Button
      type="button"
      size="sm"
      variant={state.on ? 'default' : 'outline'}
      aria-pressed={state.on}
      aria-label="보안 접속"
      title={title}
      disabled={busy}
      onClick={() => void toggle()}
    >
      {/* 색만으로 켜짐을 말하지 않는다 — 글자가 상태를 그대로 적는다. */}
      {busy ? '바꾸는 중…' : label}
    </Button>
    {(state.active_devices ?? 0) > 0 && <Button size="sm" variant="ghost" disabled={busy}
      title="같은 계정으로 승인한 모든 기기의 접속을 해제합니다."
      onClick={() => void toggle(true)}>모든 기기 끄기 ({state.active_devices})</Button>}
    </div>
  );
}
